// frontend/src/EvaluatorSupplierList.tsx - Restored and updated to flag non-compliance without blocking

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';
import COIDeclarationModal from './COIDeclarationModal';

interface EvaluatorSupplierListProps {
  processId: number;
  processName: string;
  evaluatorId: number;
  onSupplierSelect: (supplierId: number, supplierName: string) => void;
  onGridEvaluate?: () => void;
  onBack: () => void;
}

interface Supplier {
  id: number;
  name: string;
  abn_acn: string;
  evaluation_status: string;
  is_compliant: boolean;
  total_score?: number;
  mandatory_passed?: number;
  total_mandatory: number;
  last_updated?: string;
}

interface COIStatus {
  coi_required: boolean;
  coi_status: string | null;
  can_evaluate: boolean;
  message: string;
  declared_at?: string;
  reviewed_at?: string;
}

const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)'
    },
    header: {
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(10px)',
      padding: '1rem 2rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky' as 'sticky',
      top: 0,
      zIndex: 50
    },
    logoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
    },
    logo: {
        height: '160px',
        width: 'auto'
    },
    card: {
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '20px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid #f3f4f6',
      marginBottom: '2rem'
    },
    button: {
      padding: '0.75rem 1.5rem',
      background: 'linear-gradient(45deg, #2563eb, #059669)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    buttonSecondary: {
        background: 'white',
        color: '#4f46e5',
        border: '2px solid #4f46e5',
    },
};

const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>, enter: boolean) => {
    e.currentTarget.style.transform = enter ? 'translateY(-2px)' : 'translateY(0)';
    e.currentTarget.style.boxShadow = enter ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none';
};

const EvaluatorSupplierList: React.FC<EvaluatorSupplierListProps> = ({
  processId,
  processName,
  evaluatorId,
  onSupplierSelect,
  onGridEvaluate,
  onBack,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coiStatus, setCoiStatus] = useState<COIStatus | null>(null);
  const [showCoiModal, setShowCoiModal] = useState(false);

  const fetchSuppliers = useCallback(async () => {
  setIsLoading(true);
  setError(null);
  try {
    const token = Cookies.get('access_token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    
    // Fetch suppliers and COI status in parallel
    const [suppliersResponse, coiResponse] = await Promise.all([
      axios.get<Supplier[]>(
        `${API_URL}/api/evaluations/process/${processId}/evaluator/${evaluatorId}/suppliers`,
        authHeaders
      ),
      axios.get<COIStatus>(
        `${API_URL}/api/coi/process/${processId}/status`,
        authHeaders
      )
    ]);
    
    setSuppliers(suppliersResponse.data);
    setCoiStatus(coiResponse.data);
    
    // If COI is required but not declared, show modal
    if (coiResponse.data.coi_required) {
      setShowCoiModal(true);
    }
  } catch (err: any) {
    console.error('Error fetching data:', err);
    console.error('COI API Error details:', err.response);
    
    // Always require COI for new processs - no fallback to "not required"
    if (err.response?.status === 404 && err.config?.url?.includes('/api/coi/')) {
      // COI endpoint returned 404, but we still require COI
      try {
        const token = Cookies.get('access_token');
        const suppliersResponse = await axios.get<Supplier[]>(
          `${API_URL}/api/evaluations/process/${processId}/evaluator/${evaluatorId}/suppliers`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuppliers(suppliersResponse.data);
        // Set COI as required even if API call failed
        setCoiStatus({ 
          coi_required: true, 
          coi_status: null, 
          can_evaluate: false, 
          message: 'COI declaration required before evaluation can begin' 
        });
        setShowCoiModal(true); // Show COI modal immediately
      } catch (supplierErr: any) {
        setError(supplierErr.response?.data?.detail || 'Failed to load suppliers for evaluation.');
      }
    } else {
      setError(err.response?.data?.detail || 'Failed to load evaluation data.');
    }
  } finally {
    setIsLoading(false);
  }
}, [processId, evaluatorId]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const progressSummary = useMemo(() => {
    const mandatoryFailCount = suppliers.filter(s => !s.is_compliant).length;
    
    // Evaluation status is now independent of compliance
    const completedCount = suppliers.filter(s => s.evaluation_status === 'Completed').length;
    const inProgressCount = suppliers.filter(s => s.evaluation_status === 'In Progress').length;
    const notStartedCount = suppliers.filter(s => s.evaluation_status === 'Not Started').length;
    
    const totalSuppliers = suppliers.length;
    const progressPercentage = totalSuppliers > 0 ? (completedCount / totalSuppliers) * 100 : 0;

    return {
      completedCount,
      inProgressCount,
      notStartedCount,
      mandatoryFailCount,
      totalSuppliers,
      progressPercentage,
    };
  }, [suppliers]);

  const getStatusColor = useCallback((status: Supplier['evaluation_status']) => {
    switch (status) {
      case 'Not Started': return '#ef4444'; // Red
      case 'In Progress': return '#f59e0b'; // Orange
      case 'Completed': return '#10b981'; // Green
      default: return '#6b7280'; // Gray
    }
  }, []);

  const handleSupplierSelect = (supplierId: number, supplierName: string) => {
    // Check COI status before allowing evaluation
    if (coiStatus && !coiStatus.can_evaluate) {
      if (coiStatus.coi_required) {
        setShowCoiModal(true);
      } else {
        // COI is pending review
        alert(`COI Declaration Status: ${coiStatus.message}\n\nYou cannot begin evaluation until your COI declaration is approved by the Process Owner.`);
      }
      return;
    }
    
    // Proceed with supplier selection
    onSupplierSelect(supplierId, supplierName);
  };

  const handleCoiSuccess = () => {
    setShowCoiModal(false);
    // Refresh data to get updated COI status
    fetchSuppliers();
  };

  const handleCoiCancel = () => {
    setShowCoiModal(false);
    // Navigate back since COI is required
    onBack();
  };

    if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem'}}>
                <div style={{ fontSize: '1.2rem', color: '#374151' }}>Loading Suppliers...</div>
            </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
       <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem', color: '#dc2626'}}>
                <p>Error: {error}</p>
                <button onClick={onBack} style={{...styles.button, ...styles.buttonSecondary}}>← Back to Processes</button>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      <main style={{ padding: '2rem' }}>
        <div className="text-center mb-xl">
          <h2 className="page-title">Select Supplier for Evaluation</h2>
          <p className="page-subtitle">Process: {processName}</p>
        </div>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {suppliers.length === 0 ? (
            <div style={styles.card}>
                <div style={{textAlign: 'center', padding: '2rem'}}>
                    <h3 style={{fontSize: '1.5rem', fontWeight: 'bold'}}>No Suppliers Available</h3>
                    <p style={{color: '#6b7280'}}>There are no suppliers assigned to this process for evaluation.</p>
                </div>
            </div>
          ) : (
            <>
              <div style={styles.card}>
                <h2 style={{ color: '#1e293b', margin: '0 0 1.5rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>Evaluation Progress</h2>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{fontWeight: '600'}}>Overall Status</span>
                    <span style={{ fontWeight: 'bold' }}>{progressSummary.completedCount} / {progressSummary.totalSuppliers} Evaluated</span>
                  </div>
                  <div style={{ background: '#e2e8f0', borderRadius: '9999px', height: '12px', overflow: 'hidden' }}>
                    <div style={{ width: `${progressSummary.progressPercentage}%`, height: '100%', background: 'linear-gradient(45deg, #2563eb, #059669)', transition: 'width 0.5s ease-in-out' }}></div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
                  <div style={{ textAlign: 'center' }}><p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{progressSummary.completedCount}</p><p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>Completed</p></div>
                  <div style={{ textAlign: 'center' }}><p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>{progressSummary.inProgressCount}</p><p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>In Progress</p></div>
                  <div style={{ textAlign: 'center' }}><p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#64748b' }}>{progressSummary.notStartedCount}</p><p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>Not Started</p></div>
                  <div style={{ textAlign: 'center' }}><p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>{progressSummary.mandatoryFailCount}</p><p style={{ margin: 0, fontSize: '0.9rem', color: '#6b7280' }}>Non-Compliant</p></div>
                </div>
              </div>

              {/* COI Status Display */}
              {coiStatus && (
                <div style={{
                  ...styles.card,
                  backgroundColor: coiStatus.can_evaluate ? '#f0f9ff' : '#fef3c7',
                  border: `2px solid ${coiStatus.can_evaluate ? '#3b82f6' : '#f59e0b'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>
                      {coiStatus.can_evaluate ? '✅' : coiStatus.coi_required ? '⚠️' : '🕒'}
                    </span>
                    <div>
                      <h3 style={{ 
                        margin: '0 0 0.5rem 0', 
                        color: coiStatus.can_evaluate ? '#1e40af' : '#92400e',
                        fontSize: '1.1rem',
                        fontWeight: '600'
                      }}>
                        Conflict of Interest Status
                      </h3>
                      <p style={{ 
                        margin: 0, 
                        color: coiStatus.can_evaluate ? '#1e40af' : '#92400e',
                        fontSize: '0.95rem'
                      }}>
                        {coiStatus.message}
                      </p>
                      {coiStatus.declared_at && (
                        <p style={{ 
                          margin: '0.5rem 0 0 0', 
                          color: '#6b7280',
                          fontSize: '0.85rem'
                        }}>
                          Declared: {new Date(coiStatus.declared_at).toLocaleDateString()}
                          {coiStatus.reviewed_at && ` • Reviewed: ${new Date(coiStatus.reviewed_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ color: '#1e293b', fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Available Suppliers</h2>
                  {onGridEvaluate && (
                    <button 
                      onClick={onGridEvaluate}
                      style={{
                        background: 'linear-gradient(45deg, #2563eb, #1d4ed8)',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.3)';
                      }}
                    >
                      📊 Grid Evaluation View
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {suppliers.map(supplier => (
                    <div
                      key={supplier.id}
                      onClick={() => handleSupplierSelect(supplier.id, supplier.name)}
                      style={{
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: `2px solid ${!supplier.is_compliant ? '#fca5a5' : '#e2e8f0'}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: '#f9fafb'
                      }}
                       onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'; }}
                       onMouseLeave={(e) => { e.currentTarget.style.borderColor = !supplier.is_compliant ? '#fca5a5' : '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                          <div>
                              <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>{supplier.name}</h3>
                              <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>ABN/ACN: {supplier.abn_acn || 'N/A'}</p>
                          </div>
                          <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 'bold', backgroundColor: getStatusColor(supplier.evaluation_status), color: 'white' }}>
                              {supplier.evaluation_status || 'Unknown'}
                          </span>
                      </div>
                      
                      {!supplier.is_compliant && (
                          <div style={{ padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 'bold', backgroundColor: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', marginBottom: '1rem' }}>
                              Note: This supplier did not pass compliance requirements and should not be evaluated.
                          </div>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSupplierSelect(supplier.id, supplier.name);
                        }}
                        style={{...styles.button, width: '100%', marginTop: '1rem'}}
                      >
                        {supplier.evaluation_status === 'In Progress' ? 'Resume Evaluation' : 'Start Evaluation'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
           <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '2rem' }}>
                <button onClick={onBack} style={{...styles.button, ...styles.buttonSecondary}}>← Back to Processes</button>
            </div>
        </div>
      </main>
      
      {/* COI Declaration Modal */}
      {showCoiModal && (
        <COIDeclarationModal
          processId={processId}
          processName={processName}
          suppliers={suppliers}
          onSuccess={handleCoiSuccess}
          onCancel={handleCoiCancel}
        />
      )}
    </div>
  );
};

export default EvaluatorSupplierList;

